/**
 * About page API functions
 */

import qs from 'qs';

import { AboutFallbackData } from '../fallback-data/about-page';
import { AboutPageData } from '../types/about-page';
import { ABOUT_PAGE_POPULATION } from '../utils/population';
import { STRAPI_URL } from './base';

/**
 * Fetch About page data with full population using qs
 */
export async function fetchAboutPage(): Promise<AboutPageData | null> {
  try {
    // Use qs to generate the query string from the clean object
    const queryString = qs.stringify(ABOUT_PAGE_POPULATION, { encode: false });

    // Construct the full URL
    const url = `${STRAPI_URL}/api/about?${queryString}`;

    const response = await fetch(url);
    console.log('🚀 ~ fetchAboutPage ~ response:', response);

    if (!response.ok) {
      console.error('HTTP error! status:', response.status);
      return AboutFallbackData;
    }

    const data = await response.json();

    if (!data.data) {
      return AboutFallbackData;
    }

    return data.data;
  } catch (error) {
    console.error('Error fetching about page:', error);
    return null;
  }
}

/**
 * Fetch About page data with custom population levels
 */
export async function fetchAboutPageWithPopulation(
  customPopulations?: any
): Promise<AboutPageData | null> {
  try {
    // Use custom populations or default to full population
    const populations = customPopulations || ABOUT_PAGE_POPULATION;

    // Use qs to generate the query string
    const queryString = qs.stringify(populations, { encode: false });

    // Construct the full URL
    const url = `${STRAPI_URL}/api/about?${queryString}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return data.data;
  } catch (error) {
    console.error('Error fetching about page with custom population:', error);
    return null;
  }
}

/**
 * Fetch About page data with the exact population parameters specified
 * This matches the URL: localhost:1337/api/about?populate[seo]=true&populate[hero]=true&populate[mission]=true&populate[coreValues][populate][values][populate][icon]=true&populate[featuredProjects][populate][projects][populate][image]=true&populate[partners][populate][logos]=true&populate[cta]=true
 */
export async function fetchAboutPageExact(): Promise<AboutPageData | null> {
  try {
    // Use the same clean object structure but with qs
    const queryString = qs.stringify(ABOUT_PAGE_POPULATION, { encode: false });

    // Construct the full URL
    const url = `${STRAPI_URL}/api/about?${queryString}`;

    const response = await fetch(url);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('HTTP Error Response:', {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
        body: errorText,
      });
      throw new Error(
        `HTTP error! status: ${response.status} - ${response.statusText}`
      );
    }

    const data = await response.json();

    // Validate the response structure
    if (!data || !data.data) {
      throw new Error('Invalid response structure from API');
    }

    return data.data;
  } catch (error) {
    console.error('Error fetching about page with exact population:', error);
    return null;
  }
}
